/**
 * Front-end model representing an application. See back-end complement for field descriptions.
 *
 * The following Field model attribute sets are located under the static `fields` attribute.
 * * IsEnabled
 * * IsLocal
 * * Name
 * * Domain
 * * ApplicationKey
 * * Secret
 * * RedirectUrl
 * * AppStatus
 * @module app/models/Plugin
 */
import Backbone from "backbone";
import $ from "jquery";
import _ from "underscore";
import RequestUtils from "app/utils/RequestUtils";
import FieldUtils from "app/utils/FieldUtils";
var Plugin = Backbone.Model.extend(
  {
    defaults: {
      IsEnabled: false,
      IsLocal: true,
      Name: "",
      Domain: "",
      Secret: "",
      ApplicationKey: "",
      Status: null,
      menuName: "",
      adminMenuName: "",
      mapName: "",
      type: "core",
      slug: "",
      stylesheet: "",
    },

    constructor: function (attributes, options) {
      Backbone.Model.call(
        this,
        attributes,
        _.extend({}, options, {
          parse: true,
        })
      );
    },

    parse: function (response) {
      var attributes = Backbone.Model.prototype.parse.apply(this, arguments);

      attributes.Status = !_.isObject(attributes.Status)
        ? attributes.Status
        : attributes.Status.id;

      return attributes;
    },

    /**
     * Resets the application secret.
     * @return {Promise} A promise object.
     * @async
     */
    resetSecret: function () {
      var deferred = new $.Deferred();
      var promise = RequestUtils.post(this.url() + "/ResetSecret");
      var self = this;

      promise
        .done(function (data) {
          self.set(data);
          deferred.resolve.apply(deferred, arguments);
        })
        .fail(function () {
          deferred.reject.apply(deferred, arguments);
        });

      return deferred.promise();
    },

    /**
     * Generates the entry point URL for this application, given a urlBuilder.
     * @param urlBuilder The URL builder to use to construct the full URL.
     * @return {String} The URL at which the main entry point of the application is located.
     */
    mainUrl: function (urlBuilder) {
      return urlBuilder.url("/" + this.attributes.slug);
    },

    /**
     * Generates the administrative entry point URL for this application, given a urlBuilder.
     * @param urlBuilder The URL builder to use to construct the full URL.
     * @return {String} The URL at which the administrative entry point of the application is located.
     */
    adminUrl: function (urlBuilder) {
      return urlBuilder.url("/" + this.attributes.slug + "/admin");
    },

    /**
     * Gets the display text for any menus describing the main entry point of the application.
     * @return {String} The display text.
     */
    menuText: function () {
      return this.attributes.menuName || this.attributes.Name;
    },

    /**
     * Gets the display text for any map controls displaying outputs from this plugin.
     * @return {String} The display text.
     */
    mapText: function () {
      return this.get("mapName") || this.get("Name");
    },

    /**
     * Gets the display text for any menus describing the administrative entry point of the application.
     * @return {String} The display text.
     */
    adminMenuText: function () {
      return this.attributes.adminMenuName || this.menuText();
    },

    toJSON: function () {
      var json = Backbone.Model.prototype.toJSON.apply(this, arguments);

      json.Status = !_.isObject(json.Status) ? json.Status : json.Status.id;

      return json;
    },
  },
  {
    fields: {
      IsEnabled: FieldUtils.fieldPrototype("IsEnabled", "Enabled", "checkbox", {
        editable: false,
      }),
      Name: FieldUtils.fieldPrototype("Name", "Name", "text", {
        sortable: true,
        filterable: true,
      }),
      Domain: FieldUtils.fieldPrototype("Domain", "Domain", "text"),
      ApplicationKey: FieldUtils.fieldPrototype("ApplicationKey", "Key", null),
      Secret: FieldUtils.fieldPrototype("Secret", "Secret", null),
      Status: FieldUtils.fieldPrototype("Status", "Status", null, {
        values: [
          {
            value: "normal",
            text: "Normal",
          },
          {
            value: "deleted",
            text: "Deleted",
          },
          {
            value: "pending",
            text: "Pending",
          },
        ],
      }),
      menuName: FieldUtils.fieldPrototype("menuName", "Menu Name", "text"),
      adminMenuName: FieldUtils.fieldPrototype(
        "adminMenuName",
        "Administrative Menu Name",
        "text"
      ),
      mapName: FieldUtils.fieldPrototype("mapName", "Map Name", "text"),
      type: FieldUtils.fieldPrototype("type", "Type", null, {
        values: [
          {
            value: "core",
            text: "Core",
          },
          {
            value: "map",
            text: "Map",
          },
          {
            value: "both",
            text: "Both",
          },
        ],
      }),
      slug: FieldUtils.fieldPrototype("slug", "Slug", "text"),
      stylesheet: FieldUtils.fieldPrototype("stylesheet", "Stylesheet", "text"),
    },
  }
);

Plugin.fields.name = Plugin.fields.Name; //alias these two

export default Plugin;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/Plugin.js
// module id = 140
// module chunks = 1